import { useTranslation } from "react-i18next";
import { useIsMobile } from "../../../../../../utils/Functions";
import useUser from "../../../../../../hooks/useUser";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Cancel, Info } from "@mui/icons-material";

export function TierCard({ tier, onTierSelect, onDetailsClick, testId }) {
  const { t } = useTranslation();
  const { roles } = useUser();
  const theme = useTheme();
  const themeMode = theme.palette.mode;

  if (!tier) return null;

  return (
    <Card
      variant={themeMode === "dark" ? "outlined" : "elevation"}
      elevation={11}
      data-testid={testId}
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        p: 2,
        position: "relative",
        borderColor: tier.isCurrent ? "primary.main" : "divider",
        borderWidth: tier.isCurrent ? 2 : 1,
      }}
    >
      {roles.adminOrEmployee && tier.isCurrent && (
        <Chip
          label={t("CurrentPlan")}
          color="primary"
          data-testid={testId && `${testId}.current-chip`}
          sx={{
            position: "absolute",
            top: 16,
            left: 16,
            fontWeight: "bold",
          }}
        />
      )}
      <CardContent sx={{ flexGrow: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            mt: 3,
          }}
        >
          <Typography variant="h5" fontWeight="bold">
            {t(tier.title)}
          </Typography>

          <Button
            variant="text"
            size="small"
            startIcon={<Info />}
            onClick={() => onDetailsClick(tier.id)}
            sx={{ ml: 2, fontSize: "0.65rem" }} //this is added due to german text overlapping with the details button
          >
            {t("Details")}
          </Button>
        </Box>

        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
          {t(tier.description)}
        </Typography>

        {/* Features */}
        <List disablePadding>
          {tier.features.map((line) => (
            <ListItem key={line} disableGutters sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircleIcon color="primary" fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={t(line)} />
            </ListItem>
          ))}
          {tier.missingFeatures &&
            tier.missingFeatures.length > 0 &&
            tier.missingFeatures.map((feature, index) => (
              <ListItem key={index} disableGutters sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Cancel color="disabled" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t(feature)} />
              </ListItem>
            ))}
        </List>
      </CardContent>
      {roles.master && (
        <Box sx={{ p: 2, pt: 0 }}>
          <Button
            fullWidth
            variant="contained"
            color={"primary"}
            disabled={Boolean(tier.isCurrent || !roles.master)}
            onClick={() => onTierSelect(tier)}
            data-testid={testId && `${testId}.choose-button`}
          >
            {tier.buttonText}
          </Button>
        </Box>
      )}
    </Card>
  );
}
